import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import {
  ArrowRight,
  ChevronDown,
  ChevronLeft,
  CircleAlert,
  Loader2,
  MapPin,
  Mic,
  PenLine,
  Plus,
  Trash2,
} from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { toast } from 'sonner'
import { DictateButton, useDictation } from '@/components/survey/dictate-button'
import { PhotoButtons, PhotoGrid, PhotoViewer, UploadErrors } from '@/components/survey/photos'
import { TextInput } from '@/components/settings/form-kit'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Skeleton } from '@/components/ui/skeleton'
import { Textarea } from '@/components/ui/textarea'
import { api, ApiError } from '@/lib/api'
import { quoteQuery } from '@/lib/quotes'
import { can } from '@/lib/roles'
import { reportError } from '@/lib/settings'
import {
  apiDelete,
  dictationStatusQuery,
  nextAreaName,
  QUICK_AREAS,
  surveyQuery,
  usePhotoUploads,
  useUnsavedWarning,
  type Area,
  type Photo,
} from '@/lib/survey'
import { useTitle } from '@/lib/use-title'
import { cn } from '@/lib/utils'

export const Route = createFileRoute('/_app/quotes/$id_/survey')({ component: SurveyPage })

/** The on-site walk-round: one card per area with photos, notes and dictation. Phone first. */
function SurveyPage() {
  const { id } = Route.useParams()
  const { me } = Route.useRouteContext()
  const qc = useQueryClient()
  const navigate = useNavigate()
  const quote = useQuery(quoteQuery(id))
  const survey = useQuery(surveyQuery(id))
  const dictation = useQuery(dictationStatusQuery)
  const uploads = usePhotoUploads(id)
  const [openId, setOpenId] = useState<string | null>(null)
  const [viewing, setViewing] = useState<Photo | null>(null)
  const [custom, setCustom] = useState('')
  const addRef = useRef<HTMLDivElement>(null)
  useTitle(quote.data ? `Walk-round · ${quote.data.title}` : 'Walk-round')

  const areas = survey.data?.areas ?? []
  const photos = survey.data?.photos ?? []

  // Open the first area on arrival.
  useEffect(() => {
    if (openId === null && areas.length > 0) setOpenId(areas[0]!.id)
  }, [areas, openId])

  const add = useMutation({
    mutationFn: (name: string) => api.post<Area>(`/quotes/${id}/areas`, { name }),
    onSuccess: async (a) => {
      setCustom('')
      await qc.invalidateQueries({ queryKey: ['survey', id] })
      setOpenId(a.id)
      requestAnimationFrame(() =>
        document
          .getElementById(`area-${a.id}`)
          ?.scrollIntoView({ behavior: 'smooth', block: 'start' }),
      )
    },
    onError: (e) => reportError(e),
  })
  const addNamed = (base: string) => {
    if (!base.trim() || add.isPending) return
    add.mutate(
      nextAreaName(
        base,
        areas.map((a) => a.name),
      ),
    )
  }

  if (quote.isError || survey.isError)
    return (
      <p role="alert" className="text-destructive">
        {(quote.error ?? survey.error)?.message}
      </p>
    )
  if (!quote.data || !survey.data) return <Skeleton className="h-[70dvh] rounded-xl" />
  const q = quote.data
  const general = photos.filter((p) => !p.areaId)
  const siteLine = [q.site?.name, q.site?.address, q.site?.postcode].filter(Boolean).join(', ')

  return (
    <div className="mx-auto grid max-w-3xl gap-5">
      <div>
        <Link
          to="/quotes/$id"
          params={{ id }}
          className="mb-2 inline-flex min-h-11 items-center gap-1 text-sm font-medium text-muted-foreground hover:text-foreground"
        >
          <ChevronLeft className="size-4" aria-hidden />
          {q.reference}
        </Link>
        <h1 className="text-2xl font-semibold md:text-[28px]">Site walk-round</h1>
        <p className="mt-1 text-[15px] text-muted-foreground">
          <span className="font-medium text-foreground">{q.customerName}</span>
          {siteLine && (
            <span className="mt-0.5 flex items-start gap-1">
              <MapPin className="mt-0.5 size-4 shrink-0" aria-hidden />
              {siteLine}
            </span>
          )}
        </p>
      </div>

      <p className="rounded-lg border bg-muted/60 px-4 py-3 text-sm">
        Go room by room. Add an area, take photos, then type or dictate what needs doing. Everything
        saves as you go.
      </p>
      {dictation.data && !dictation.data.enabled && (
        <p className="flex items-start gap-2 rounded-lg border px-4 py-3 text-sm">
          <Mic className="mt-0.5 size-4 shrink-0 text-muted-foreground" aria-hidden />
          <span>
            Dictation is not switched on, so notes are typed for now.{' '}
            {can.manageSettings(me) && (
              <Link
                to="/settings/$slug"
                params={{ slug: 'dictation' }}
                className="font-medium text-primary underline-offset-4 hover:underline"
              >
                Set up dictation
              </Link>
            )}
          </span>
        </p>
      )}

      {areas.length === 0 ? (
        <p className="rounded-xl border border-dashed bg-card px-5 py-8 text-center text-[15px] text-muted-foreground">
          No areas yet. Start with the first room below.
        </p>
      ) : (
        <ol className="grid gap-3" aria-label="Areas">
          {areas.map((a) => (
            <li key={a.id} id={`area-${a.id}`} className="scroll-mt-20">
              <AreaCard
                area={a}
                quoteId={id}
                open={openId === a.id}
                onToggle={() => setOpenId(openId === a.id ? '' : a.id)}
                photos={photos.filter((p) => p.areaId === a.id)}
                uploads={uploads}
                onOpenPhoto={setViewing}
              />
            </li>
          ))}
        </ol>
      )}

      <section
        ref={addRef}
        aria-labelledby="add-area"
        className="grid scroll-mt-20 gap-3 rounded-xl border bg-card p-4"
      >
        <h2 id="add-area" className="text-[15px] font-semibold">
          Add an area
        </h2>
        <div className="flex flex-wrap gap-2">
          {QUICK_AREAS.map((n) => (
            <Button
              key={n}
              type="button"
              variant="outline"
              className="h-11 rounded-full px-4"
              disabled={add.isPending}
              onClick={() => addNamed(n)}
            >
              <Plus />
              {n}
            </Button>
          ))}
        </div>
        <form
          className="grid grid-cols-[minmax(0,1fr)_auto] gap-2"
          onSubmit={(e) => {
            e.preventDefault()
            addNamed(custom)
          }}
        >
          <TextInput
            aria-label="Area name"
            placeholder="Another area, e.g. Boardroom"
            value={custom}
            maxLength={80}
            onChange={(e) => setCustom(e.target.value)}
          />
          <Button type="submit" className="h-11 md:h-10" disabled={!custom.trim() || add.isPending}>
            {add.isPending ? <Loader2 className="animate-spin" /> : <Plus />}
            Add
          </Button>
        </form>
      </section>

      <section
        aria-labelledby="general-photos"
        className="grid gap-3 rounded-xl border bg-card p-4"
      >
        <div>
          <h2 id="general-photos" className="text-[15px] font-semibold">
            Other photos
          </h2>
          <p className="text-sm text-muted-foreground">
            Outside, parking, the building, anything not in one area.
          </p>
        </div>
        <PhotoGrid
          photos={general}
          pending={uploads.items.filter((u) => !u.areaId)}
          label="Other photos"
          onOpen={setViewing}
          onRetry={uploads.retry}
          onDismiss={uploads.dismiss}
        />
        <UploadErrors pending={uploads.items.filter((u) => !u.areaId)} />
        <PhotoButtons compact onFiles={(f) => uploads.add(f, null)} />
      </section>

      <div className="sticky bottom-[calc(4.25rem+env(safe-area-inset-bottom))] z-20 grid grid-cols-[auto_minmax(0,1fr)] gap-2 rounded-xl border bg-card/95 p-2 shadow-lg backdrop-blur md:bottom-4">
        <Button
          type="button"
          variant="outline"
          className="h-12"
          onClick={() => {
            addRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
            addRef.current?.querySelector('input')?.focus({ preventScroll: true })
          }}
        >
          <Plus />
          Area
        </Button>
        <Button
          type="button"
          className="h-12"
          onClick={() => navigate({ to: '/quotes/$id', params: { id } })}
        >
          Continue to pricing
          <ArrowRight />
        </Button>
      </div>

      <PhotoViewer photo={viewing} quoteId={id} areas={areas} onClose={() => setViewing(null)} />
    </div>
  )
}

function AreaCard({
  area,
  quoteId,
  open,
  onToggle,
  photos,
  uploads,
  onOpenPhoto,
}: {
  area: Area
  quoteId: string
  open: boolean
  onToggle: () => void
  photos: Photo[]
  uploads: ReturnType<typeof usePhotoUploads>
  onOpenPhoto: (p: Photo) => void
}) {
  const qc = useQueryClient()
  const save = useAreaAutosave(area, quoteId)
  const dictation = useDictation(save.notes, save.change)
  const [renaming, setRenaming] = useState(false)
  const [name, setName] = useState(area.name)
  const [deleting, setDeleting] = useState(false)
  const pending = uploads.items.filter((u) => u.areaId === area.id)
  const bodyId = `area-body-${area.id}`

  const remove = useMutation({
    mutationFn: () => apiDelete(`/survey-areas/${area.id}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['survey', quoteId] })
      void qc.invalidateQueries({ queryKey: ['quote', quoteId] })
      toast.success(`${area.name} deleted`)
    },
    onError: (e) => reportError(e),
  })

  const count = photos.length + pending.length
  return (
    <article className={cn('rounded-xl border bg-card', open && 'ring-1 ring-primary/30')}>
      <h2 className="text-[15px]">
        <button
          type="button"
          onClick={onToggle}
          aria-expanded={open}
          aria-controls={bodyId}
          className="flex min-h-14 w-full items-center gap-3 px-4 py-3 text-left"
        >
          <span className="min-w-0 flex-1">
            <span className="block truncate text-base font-semibold">{area.name}</span>
            <span className="block text-[13px] font-normal text-muted-foreground">
              {count === 1 ? '1 photo' : `${count} photos`}
              {save.notes.trim() ? ' · notes added' : ''}
            </span>
          </span>
          <SaveState state={save.state} />
          <ChevronDown
            className={cn('size-5 shrink-0 transition-transform', open && 'rotate-180')}
            aria-hidden
          />
        </button>
      </h2>
      {open && (
        <div id={bodyId} className="grid gap-4 border-t px-4 pt-4 pb-4">
          <PhotoGrid
            photos={photos}
            pending={pending}
            label={`Photos of ${area.name}`}
            onOpen={onOpenPhoto}
            onRetry={uploads.retry}
            onDismiss={uploads.dismiss}
          />
          <UploadErrors pending={pending} />
          <PhotoButtons onFiles={(f) => uploads.add(f, area.id)} />

          <div className="grid gap-2">
            <label htmlFor={`notes-${area.id}`} className="text-sm font-medium">
              Notes for {area.name}
            </label>
            <Textarea
              id={`notes-${area.id}`}
              rows={5}
              value={save.notes}
              onChange={(e) => save.change(e.target.value)}
              onBlur={() => save.flush()}
              placeholder="What needs cleaning, how often, floor types, anything unusual…"
              className="min-h-32 text-base md:text-[15px]"
            />
            {dictation.undo}
            {save.state === 'error' && (
              <p role="alert" className="flex items-center gap-2 text-[13px] text-destructive">
                <CircleAlert className="size-4" aria-hidden />
                Not saved yet: {save.error}
                <button
                  type="button"
                  className="min-h-11 font-medium underline"
                  onClick={() => save.flush()}
                >
                  Try again
                </button>
              </p>
            )}
            <DictateButton hint={area.name} onText={dictation.onText} label="Dictate notes" />
          </div>

          <div className="flex flex-wrap gap-2 border-t pt-3">
            <Button
              type="button"
              variant="ghost"
              className="h-11"
              onClick={() => {
                setName(area.name)
                setRenaming(true)
              }}
            >
              <PenLine />
              Rename
            </Button>
            <Button
              type="button"
              variant="ghost"
              className="h-11 text-destructive"
              onClick={() => setDeleting(true)}
            >
              <Trash2 />
              Delete area
            </Button>
          </div>
        </div>
      )}

      <Dialog open={renaming} onOpenChange={setRenaming}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>Rename area</DialogTitle>
            <DialogDescription className="sr-only">Give this area a new name.</DialogDescription>
          </DialogHeader>
          <form
            className="grid gap-4"
            onSubmit={(e) => {
              e.preventDefault()
              if (name.trim()) void save.rename(name.trim()).then((ok) => ok && setRenaming(false))
            }}
          >
            <TextInput
              aria-label="Area name"
              value={name}
              maxLength={80}
              autoFocus
              onChange={(e) => setName(e.target.value)}
            />
            <DialogFooter className="gap-2">
              <Button
                type="button"
                variant="outline"
                className="h-11 md:h-10"
                onClick={() => setRenaming(false)}
              >
                Cancel
              </Button>
              <Button type="submit" className="h-11 md:h-10" disabled={!name.trim()}>
                Save
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={deleting} onOpenChange={setDeleting}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>Delete {area.name}?</DialogTitle>
            <DialogDescription>
              {photos.length > 0
                ? `Its notes and ${photos.length === 1 ? '1 photo' : `${photos.length} photos`} will be deleted too.`
                : 'Its notes will be deleted too.'}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button
              type="button"
              variant="outline"
              className="h-11 md:h-10"
              onClick={() => setDeleting(false)}
            >
              Keep
            </Button>
            <Button
              type="button"
              variant="destructive"
              className="h-11 md:h-10"
              disabled={remove.isPending}
              onClick={() => remove.mutate(undefined, { onSuccess: () => setDeleting(false) })}
            >
              {remove.isPending && <Loader2 className="animate-spin" />}
              Delete area
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </article>
  )
}

function SaveState({ state }: { state: SaveStateKind }) {
  if (state === 'saved') return null
  return (
    <span
      className={cn(
        'inline-flex shrink-0 items-center gap-1 text-[12px] font-medium',
        state === 'error' ? 'text-destructive' : 'text-muted-foreground',
      )}
      role="status"
    >
      {state === 'saving' ? (
        <Loader2 className="size-3.5 animate-spin" aria-hidden />
      ) : state === 'error' ? (
        <CircleAlert className="size-3.5" aria-hidden />
      ) : (
        <span className="size-2 rounded-full bg-muted-foreground" aria-hidden />
      )}
      {state === 'saving' ? 'Saving' : state === 'error' ? 'Not saved' : 'Editing'}
    </span>
  )
}

type SaveStateKind = 'saved' | 'unsaved' | 'saving' | 'error'

/**
 * Notes save themselves shortly after typing stops, on blur, and before the
 * page is hidden. Saves run one at a time; a version conflict is refetched
 * and retried with the text on screen (the person here is the one typing).
 */
function useAreaAutosave(area: Area, quoteId: string) {
  const qc = useQueryClient()
  const [notes, setNotes] = useState(area.notes)
  const [state, setState] = useState<SaveStateKind>('saved')
  const [error, setError] = useState('')
  const version = useRef(area.version)
  const latest = useRef(area.notes)
  const saved = useRef(area.notes)
  const busy = useRef(false)
  const timer = useRef<number | undefined>(undefined)

  // Take newer server text when nothing local is waiting to save.
  useEffect(() => {
    if (area.version > version.current && latest.current === saved.current) {
      version.current = area.version
      latest.current = area.notes
      saved.current = area.notes
      setNotes(area.notes)
    }
  }, [area.version, area.notes])

  const updateCache = (a: Area) =>
    qc.setQueryData(surveyQuery(quoteId).queryKey, (s) =>
      s ? { ...s, areas: s.areas.map((x) => (x.id === a.id ? { ...x, ...a } : x)) } : s,
    )

  async function patch(body: Record<string, unknown>): Promise<Area> {
    for (let attempt = 0; ; attempt++) {
      try {
        const a = await api.patch<Area>(`/survey-areas/${area.id}`, {
          ...body,
          expectedVersion: version.current,
        })
        version.current = a.version
        updateCache(a)
        return a
      } catch (err) {
        if (err instanceof ApiError && err.status === 409 && attempt < 2) {
          const s = await qc.fetchQuery({ ...surveyQuery(quoteId), staleTime: 0 })
          const fresh = s.areas.find((x) => x.id === area.id)
          if (!fresh) throw err
          version.current = fresh.version
          continue
        }
        throw err
      }
    }
  }

  async function flush() {
    window.clearTimeout(timer.current)
    if (busy.current) return
    busy.current = true
    try {
      while (latest.current !== saved.current) {
        const text = latest.current
        setState('saving')
        await patch({ notes: text })
        saved.current = text
      }
      setState('saved')
      setError('')
    } catch (err) {
      setState('error')
      setError(err instanceof Error ? err.message : 'Could not save.')
    } finally {
      busy.current = false
    }
  }

  function change(v: string) {
    latest.current = v
    setNotes(v)
    setState('unsaved')
    window.clearTimeout(timer.current)
    timer.current = window.setTimeout(() => void flush(), 900)
  }

  async function rename(name: string) {
    try {
      await patch({ name })
      toast.success('Area renamed')
      return true
    } catch (err) {
      reportError(err)
      return false
    }
  }

  useEffect(() => {
    const onHide = () => {
      if (document.visibilityState === 'hidden') void flush()
    }
    document.addEventListener('visibilitychange', onHide)
    return () => {
      document.removeEventListener('visibilitychange', onHide)
      if (latest.current !== saved.current) void flush()
    }
    // flush reads refs only
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useUnsavedWarning(state !== 'saved')
  return { notes, change, flush, rename, state, error }
}
